import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { HelpHint } from '@/components/common/help/HelpHint'
import { HelpText } from '@/components/common/help/HelpText'
import { helpKey } from '@/lib/action-modules/help'
import type { ScheduleTimePeriod } from '@/lib/action-modules/registry/scheduling/types'

export interface ScheduleTimeDefinitionEditorProps {
  timeModel: ScheduleTimePeriod[]
  onUpdateTimeModel: (value: ScheduleTimePeriod[]) => void
}

export function ScheduleTimeDefinitionEditor(props: ScheduleTimeDefinitionEditorProps) {
  const { t } = useTranslation('action-modules-wizard')
  const sd = (key: string) => t(`schedulingStrategy.scheduleDefinition.${key}`)
  const onAdd = () => props.onUpdateTimeModel([...props.timeModel, { startTime: null, endTime: null }])

  const update = (period: ScheduleTimePeriod, patch: Partial<ScheduleTimePeriod>) =>
    props.onUpdateTimeModel(props.timeModel.map(tm => (tm === period ? { ...tm, ...patch } : tm)))

  return (
    <div className="flex flex-col gap-2 pt-2">
      <HelpText helpKey={helpKey('schedulingTimePeriods')} />
      {/* Column headers for the date inputs, which otherwise carry only an aria-label. */}
      {props.timeModel.length > 0 && (
        <div className="flex items-center gap-2">
          <span className="flex-1 text-xs font-medium text-muted-foreground">{sd('time.start')}</span>
          <span className="flex-1 text-xs font-medium text-muted-foreground">{sd('time.end')}</span>
          <HelpHint helpKey={helpKey('schedulingTimePeriodRange')} />
        </div>
      )}
      {props.timeModel.map((m, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            type="date"
            className="flex-1"
            value={m.startTime ?? ''}
            onChange={e => update(m, { startTime: e.target.value || null })}
            aria-label={sd('time.start')}
          />
          <Input
            type="date"
            className="flex-1"
            value={m.endTime ?? ''}
            onChange={e => update(m, { endTime: e.target.value || null })}
            aria-label={sd('time.end')}
          />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" className="self-start" onClick={onAdd}>
        <Plus className="size-4" /> {sd('time.add')}
      </Button>
    </div>
  )
}
